import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AssetManagement } from '@aptsi-types';
import { DataAssetSourceService } from '../services/data-asset-source.service';

export interface DataAssetsViewDialogData {
  assetSourceId: string;
  assetSourceName?: string;
}

interface DataAssetPaginationMeta extends AssetManagement {
  totalDataAssets?: number;
  totalPages?: number;
  currentPage?: number;
}

interface AssetSearchHeader {
  name: string;
  property: string;
  showInDropdown: boolean;
}

@Component({
  selector: 'app-data-assets-view',
  templateUrl: './data-assets-view.component.html',
  styleUrls: ['./data-assets-view.component.scss']
})
export class DataAssetsViewComponent implements OnInit {
  readonly itemsPerPage = 10;

  currentPage = 1;
  totalPages = 0;
  totalPagesNormal = 0;
  totalPagesSearch = 0;
  totalItemsNormal = 0;
  totalItemsSearch = 0;

  isLoading = true;
  isSearching = false;
  loadError = false;
  searchError = false;
  issearch = false;

  searchText = '';
  filterByHeader = 'all';

  filteredData: AssetManagement[] = [];
  searchedData: AssetManagement[] = [];

  /** Ensures only the most recently requested page can update the view. */
  private requestSequence = 0;

  readonly headers: AssetSearchHeader[] = [
    { name: 'Asset Name', property: 'dataAssetName', showInDropdown: true },
    { name: 'Description', property: 'dataAssetDescription', showInDropdown: true },
    { name: 'Owner / Created By', property: 'dataAssetOwner', showInDropdown: true },
    { name: 'Status', property: 'dataAssetStatus', showInDropdown: true },
    { name: 'Lifecycle Status', property: 'dataAssetLifecycleStatus', showInDropdown: true },
    { name: 'Classification', property: 'dataClassification', showInDropdown: true },
    { name: 'Technical Name', property: 'assetTechnicalName', showInDropdown: true },
    { name: 'Qualified Name / Endpoint', property: 'qualifiedName', showInDropdown: true },
    { name: 'Collection ID', property: 'collectionId', showInDropdown: true },
    { name: 'Unique Identifier', property: 'uniqueIdentifier', showInDropdown: true },
    { name: 'Imported By', property: 'importedBy', showInDropdown: true },
    { name: 'Imported At', property: 'importedAt', showInDropdown: true }
  ];

  private readonly fieldAliases: Record<string, string[]> = {
    dataAssetName: [
      'DATAAssetName', 'dataAssetName', 'assetName', 'asset_name', 'name'
    ],
    dataAssetId: [
      'DATAAssetId', 'dataAssetId', 'assetId', 'asset_id', 'uniqueIdentifier', 'qualifiedName'
    ],
    dataAssetDescription: [
      'DATAAssetDescription', 'dataAssetDescription', 'assetDescription', 'description'
    ],
    dataAssetOwner: [
      'DATAAssetOwner', 'dataAssetOwner', 'assetOwner', 'owner', 'createdBy', 'importedBy'
    ],
    dataAssetStatus: [
      'DATAAssetStatus', 'dataAssetStatus', 'assetStatus', 'status', 'entityStatus'
    ],
    dataAssetLifecycleStatus: [
      'DATAAssetLifecycleStatus', 'dataAssetLifecycleStatus', 'assetLifecycleStatus',
      'lifecycleStatus'
    ],
    dataClassification: [
      'dataClassification', 'DataClassification', 'DATAAssetDataClassification',
      'classification', 'classificationName'
    ],
    dataAssetBusinessValue: [
      'DATAAssetBusinessValue', 'dataAssetBusinessValue', 'assetBusinessValue', 'businessValue'
    ],
    dataAssetEndpoint: [
      'DATAAssetEndpoint', 'dataAssetEndpoint', 'endpoint', 'qualifiedName', 'url'
    ],
    assetTechnicalName: ['assetTechnicalName', 'technicalName', 'DATAAssetTechnicalName'],
    qualifiedName: ['qualifiedName', 'DATAAssetEndpoint', 'dataAssetEndpoint', 'endpoint', 'url'],
    collectionId: ['collectionId', 'collectionID'],
    uniqueIdentifier: ['uniqueIdentifier', 'DATAAssetId', 'dataAssetId', 'assetId', 'asset_id'],
    importedBy: ['importedBy', 'createdBy', 'DATAAssetCreatedBy', 'dataAssetCreatedBy'],
    importedAt: ['importedAt', 'createdAt'],
    createdBy: ['createdBy', 'DATAAssetOwner', 'dataAssetOwner', 'owner'],
    entityVersion: ['entityVersion']
  };

  get displayedData(): AssetManagement[] {
    return this.issearch ? this.searchedData : this.filteredData;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: DataAssetsViewDialogData,
    private readonly dialogRef: MatDialogRef<DataAssetsViewComponent>,
    private readonly dataAssetSourceService: DataAssetSourceService
  ) {}

  ngOnInit(): void {
    if (!this.data?.assetSourceId?.trim()) {
      this.loadError = true;
      this.isLoading = false;
      return;
    }

    void this.fetchDataAssets();
  }

  async fetchDataAssets(): Promise<void> {
    const requestId = ++this.requestSequence;
    this.isLoading = true;
    this.isSearching = false;
    this.loadError = false;
    this.searchError = false;

    try {
      const apiResponse = await this.dataAssetSourceService
        .FetchDataAssetsByAssetSourceID(this.data.assetSourceId, this.currentPage - 1)
        .toPromise();

      // Ignore an older request if the user has already navigated or started a search.
      if (requestId !== this.requestSequence) {
        return;
      }

      const response: AssetManagement[] = Array.isArray(apiResponse) ? apiResponse : [];
      const metadata = response[0] as DataAssetPaginationMeta | undefined;
      this.filteredData = response;

      this.totalItemsNormal = this.readNonNegativeNumber(
        metadata?.totalDataAssets,
        response.length
      );
      // The backend is the source of truth for the number of pages.
      this.totalPagesNormal = this.readNonNegativeNumber(metadata?.totalPages, 0);
      this.totalPages = this.totalPagesNormal;

      if (this.totalPagesNormal === 0) {
        this.currentPage = 1;
      } else if (this.currentPage > this.totalPagesNormal) {
        // If the available page count shrank, return to the last valid page.
        this.currentPage = this.totalPagesNormal;
        await this.fetchDataAssets();
      }
    } catch (error) {
      if (requestId !== this.requestSequence) {
        return;
      }

      console.error('Error fetching data assets for asset source:', error);
      this.loadError = true;
      this.filteredData = [];
      this.totalItemsNormal = 0;
      this.totalPagesNormal = 0;
      this.totalPages = 0;
    } finally {
      if (requestId === this.requestSequence) {
        this.isLoading = false;
        this.isSearching = false;
      }
    }
  }

  async fetchDataAssetsSearch(): Promise<void> {
    const requestId = ++this.requestSequence;
    this.isSearching = true;
    this.isLoading = false;
    this.searchError = false;

    try {
      const apiResponse = await this.dataAssetSourceService
        .FetchDataAssetsByAssetSourceIDSearch(
          this.data.assetSourceId,
          this.currentPage - 1,
          this.searchText.trim(),
          this.filterByHeader
        )
        .toPromise();

      // Ignore an older request if a newer search or page request has started.
      if (requestId !== this.requestSequence) {
        return;
      }

      const response: AssetManagement[] = Array.isArray(apiResponse) ? apiResponse : [];
      const metadata = response[0] as DataAssetPaginationMeta | undefined;

      // The search API already applies the query and returns a paginated result.
      // Do not filter this page again on the client, as that can hide valid rows.
      this.searchedData = response;

      this.totalItemsSearch = this.readNonNegativeNumber(
        metadata?.totalDataAssets,
        response.length
      );
      // Use the total page count returned by the backend; do not recalculate it locally.
      this.totalPagesSearch = this.readNonNegativeNumber(metadata?.totalPages, 0);
      this.totalPages = this.totalPagesSearch;

      if (this.totalPagesSearch === 0) {
        this.currentPage = 1;
      } else if (this.currentPage > this.totalPagesSearch) {
        this.currentPage = this.totalPagesSearch;
        await this.fetchDataAssetsSearch();
      }
    } catch (error) {
      if (requestId !== this.requestSequence) {
        return;
      }

      console.error('Error searching data assets for asset source:', error);
      this.searchError = true;
      this.searchedData = [];
      this.totalItemsSearch = 0;
      this.totalPagesSearch = 0;
      this.totalPages = 0;
    } finally {
      if (requestId === this.requestSequence) {
        this.isSearching = false;
        this.isLoading = false;
      }
    }
  }

  async searchDataAssets(): Promise<void> {
    const query = this.searchText.trim();

    if (!query) {
      await this.clearSearch();
      return;
    }

    this.searchText = query;
    this.issearch = true;
    this.currentPage = 1;
    this.totalItemsSearch = 0;
    this.totalPagesSearch = 0;
    await this.fetchDataAssetsSearch();
  }

  async clearSearch(): Promise<void> {
    this.searchText = '';
    this.issearch = false;
    this.searchError = false;
    this.totalItemsSearch = 0;
    this.totalPagesSearch = 0;
    this.currentPage = 1;
    await this.fetchDataAssets();
  }

  onInputChange(event: Event): void {
    this.searchText = (event.target as HTMLInputElement).value;
  }

  // Keep the category-filter interaction consistent with the system-assets dialog.
  onFilterChange(): void {
    void this.clearSearch();
  }

  changePage(page: number): void {
    const activeTotalPages = this.issearch ? this.totalPagesSearch : this.totalPagesNormal;

    if (
      !Number.isInteger(page) ||
      page < 1 ||
      page > activeTotalPages ||
      page === this.currentPage
    ) {
      return;
    }

    this.currentPage = page;
    if (this.issearch) {
      void this.fetchDataAssetsSearch();
    } else {
      void this.fetchDataAssets();
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  trackByDataAssetId = (
  index: number,
  asset: AssetManagement
): string | number => {
  const record = asset as unknown as Record<string, unknown>;

  const keys = [
    'assetId',
    'dataAssetId',
    'DATAAssetId',
    'asset_id',
    'uniqueIdentifier',
    'qualifiedName',
    'assetTechnicalName',
    'name'
  ];

  for (const key of keys) {
    const value = record[key];

    if (
      value !== null &&
      value !== undefined &&
      String(value).trim() !== ''
    ) {
      return String(value);
    }
  }

  return index;
};

  /** Resolves differences between manually created fields and Purview metadata fields. */
  getAssetValue(asset: AssetManagement, property: string): string {
    const aliases = this.fieldAliases[property] ?? [property];
    return this.getFirstAssetValue(asset, aliases) || '—';
  }

  private getFirstAssetValue(asset: AssetManagement, keys: string[]): string {
    const record = asset as unknown as Record<string, unknown>;

    for (const key of keys) {
      const value = record[key];
      if (value === null || value === undefined) {
        continue;
      }

      const normalized = this.normalizeDisplayValue(value);
      if (normalized) {
        return normalized;
      }
    }

    return '';
  }

  private normalizeDisplayValue(value: unknown): string {
    if (Array.isArray(value)) {
      return value.map(item => String(item)).filter(Boolean).join(', ');
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed || trimmed === '[]' || trimmed === 'null' || trimmed === 'undefined') {
        return '';
      }
      // Purview classification fields may arrive serialized as a JSON array.
      if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
        try {
          const parsed: unknown = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            return parsed.map(item => String(item)).filter(Boolean).join(', ');
          }
        } catch {
          // Leave non-JSON bracketed strings as-is.
        }
      }
      return trimmed;
    }

    return String(value);
  }

  private readNonNegativeNumber(value: unknown, fallback: number): number {
    if (value === null || value === undefined || (typeof value === 'string' && !value.trim())) {
      return fallback;
    }

    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback;
  }
}
